import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { debounce } from '@mui/material/utils';
import { Translate } from '../../../../../shared/LanguageService';
import { isGoogleMapsAvailable } from '../state/useAddressLocation';

const SEARCH_DELAY_IN_MILLISECONDS = 700;

type Suggestion = {
    description: string;
    place_id: string;
    structured_formatting: { main_text: string; secondary_text: string };
};

export type PlaceDetails = {
    address_components: Array<{ long_name: string; types: string[] }>;
};

type AutocompleteService = {
    getPlacePredictions: (
        request: { input: string; componentRestrictions: { country: string[] } },
        callback: (results: Suggestion[] | null) => void
    ) => void;
};

type PlacesService = {
    getDetails: (
        request: { placeId: string },
        callback: (place: PlaceDetails | null, status: string) => void
    ) => void;
};

type AddressSearchProps = {
    onChoose: (place: PlaceDetails) => void;
};

function countryCode(): string {
    return window.appConfig.site === 'zentuvo' ? 'fi' : 'se';
}

export function AddressSearch({ onChoose }: AddressSearchProps) {
    let [suggestions, setSuggestions] = React.useState<Suggestion[]>([]);
    let autocompleteService = React.useRef<AutocompleteService | null>(null);
    let placesService = React.useRef<PlacesService | null>(null);

    React.useEffect(() => {
        if (isGoogleMapsAvailable() && window.google.maps.places) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            placesService.current = new window.google.maps.places.PlacesService(
                document.createElement('div')
            );
        }
    }, []);

    let findSuggestions = React.useMemo(
        () =>
            debounce((input: string, callback: (results: Suggestion[]) => void) => {
                if (!autocompleteService.current) {
                    return;
                }
                autocompleteService.current.getPlacePredictions(
                    { input, componentRestrictions: { country: [countryCode()] } },
                    (results) => callback(results || [])
                );
            }, SEARCH_DELAY_IN_MILLISECONDS),
        []
    );

    function search(input: string) {
        if (input) {
            findSuggestions(input, setSuggestions);
        }
    }

    function choose(suggestion: Suggestion | null) {
        if (!suggestion || !placesService.current) {
            return;
        }
        placesService.current.getDetails({ placeId: suggestion.place_id }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                onChoose(place);
            }
        });
    }

    return (
        <Autocomplete
            id="create-order-address-search"
            fullWidth
            options={suggestions}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.description}
            isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
            noOptionsText={Translate('order/create/v2/address-search/no-options')}
            onChange={(event, suggestion) => choose(suggestion)}
            onInputChange={(event, input) => search(input)}
            renderInput={(params) => (
                <TextField {...params} label={Translate('order/create/v2/address-search/label')} />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.place_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        <Box>
                            <Typography sx={{ fontSize: '14px' }}>
                                {option.structured_formatting.main_text}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                {option.structured_formatting.secondary_text}
                            </Typography>
                        </Box>
                    </Box>
                </li>
            )}
        />
    );
}
